/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: BI Cloud (C) Copyright IBM Corp. 2019, 2020
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
define([
	'../../../../app/nls/StringResources',
	'../../../../dashboard/util/ContentRegistryUtil',
	'../../../../lib/@waca/dashboard-common/dist/ui/interaction/Utils',
	'../../../../lib/@waca/dashboard-common/dist/core/APIFactory',
	'../../../../lib/@waca/dashboard-common/dist/api/ContentActionsProviderAPI'
], function (stringResources, ContentRegistryUtil, Utils, APIFactory, ContentActionsProviderAPI) {

	class EditTitleAction {
		constructor({ features }) {
			if(features) {
				this.dashboard = features.API;
				features.ContentActions.registerProvider('editTitle', this.getAPI());
				this._icons = features.Icons;
			}
		}

		getAPI() {
			if (!this._api) {
				this._api = APIFactory.createAPI(this, [ContentActionsProviderAPI]);
			}
			return this._api;
		}

		getLifeCycleHandlers() {
			return [{
				name: 'post:dashboard.initialize',
				action: this.postDashboardInitialize.bind(this)
			}];
		}

		postDashboardInitialize() {
			this.controller = this.dashboard.getFeature('InteractionController.internal');
			return Promise.resolve();
		}

		destroy() {
			this.dashboard = null;
			this.controller = null;
		}

		getNodes(idList) {
			return Utils.getNodes(this.controller, idList);
		}

		isEnabled(idList) {
			// Make sure edit title action is supported for the widget
			if (idList.length === 1) {
				this.content = this.dashboard.getCanvas().getContent(idList[0]);
				const capabilitiesFeature = ContentRegistryUtil.getCapabilities(this.content);
				let supportsTitle;
				if (capabilitiesFeature) {
					supportsTitle = capabilitiesFeature.getCapabilities().title;
				} else {
					const node = this.getNodes(idList)[0];
					supportsTitle = node._layout.supportsTitle;
				}
				const authoringMode = this.dashboard.getMode() === this.dashboard.MODES.EDIT;
				const { editTitle } = this.dashboard.getAppConfig('interactions') || {};
				const canEditTitle = editTitle === undefined || editTitle === true || editTitle === 'true';
				const state = this.content && this.content.getFeature('state');
				const hasError = Boolean(state && !!state.getError());

				return supportsTitle && canEditTitle && authoringMode && !hasError;
			}
			return false;
		}

		getContentActionList(idList) {
			if (this.isEnabled(idList)) {
				return [{
					name: 'editTitle',
					label: stringResources.get('toolbarActionEditTitle'),
					icon: this._icons.getIcon('text-creation').id,
					type: 'Button',
					actions: {
						apply: this.editTitle.bind(this, idList)
					}
				}];
			}
			return [];
		}

		/**
		 * Edit the widget's title
		 * @param {String[]} idList Array of model id list.
		 */
		editTitle(idList) {
			// Setting to false first to force focus in case the title is already shown
			if(this.dashboard) {
				const featureChecker = this.dashboard.getGlassCoreSvc('.FeatureChecker');
				if(featureChecker) {
					const isSmartTitleEnabled = !featureChecker.checkValue('dashboard', 'SmartTitle', 'disabled');

					const content = this.dashboard.getCanvas().getContent(idList[0]);
					if( !isSmartTitleEnabled ) {
						content.setPropertyValue('showTitle', false);
						content.setPropertyValue('showTitle', true);
					} else {
						let titleMode = content.getPropertyValue('titleMode');
						if( titleMode === 'noTitle') {
							content.setPropertyValue('titleMode', 'customTitle');
						} else {
							const titleFeature = content.getFeature('WidgetTitleTruncate');
							if(titleFeature) {
								titleFeature.highlightTitle();
							}
						}
					}
				}
			}
		}

		/**
		 * wrap the widget's title
		 * @param {String[]} idList Array of model id list.
		 */
		wrapTitle(idList) {
			// Setting to false first to force focus in case the title is already shown
			const value = this.dashboard.getCanvas().getContent(idList[0]).getPropertyValue('truncateTitle' );
			this.dashboard.getCanvas().getContent(idList[0]).setPropertyValue('truncateTitle', !value );
			this.dashboard.getCanvas().getContent(idList[0]).setPropertyValue('titleMode', 'customTitle');
		}
	}

	return EditTitleAction;

});
